import { useState, useEffect, useRef } from "react";
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/outsourced`;
const PRIMARY = "bg-[#5990DC]";

const systems = ['TB', 'YDL', 'WF', 'MC'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const defaultPeriod = () => {
  const n = new Date();
  return { mode: 'month', month: n.getMonth() + 1, year: n.getFullYear(), day: ymd(n), from: '', to: '' };
};

function periodBounds(p) {
  if (p.mode === 'month') {
    const last = new Date(p.year, p.month, 0).getDate();
    return { from: `${p.year}-${pad(p.month)}-01`, to: `${p.year}-${pad(p.month)}-${pad(last)}` };
  }
  if (p.mode === 'year') return { from: `${p.year}-01-01`, to: `${p.year}-12-31` };
  if (p.mode === 'day') return { from: p.day || '0000-01-01', to: p.day || '9999-12-31' };
  return { from: p.from || '0000-01-01', to: p.to || '9999-12-31' };
}

function periodLabel(p) {
  if (p.mode === 'month') return `${monthNames[p.month - 1]} ${p.year}`;
  if (p.mode === 'year') return `${p.year}`;
  if (p.mode === 'day') return p.day || 'Select a day';
  return `${p.from || 'start'} to ${p.to || 'today'}`;
}

function periodFileTag(p) {
  if (p.mode === 'month') return `${p.year}-${pad(p.month)}`;
  if (p.mode === 'year') return `${p.year}`;
  if (p.mode === 'day') return p.day || 'day';
  return `${p.from || 'start'}_to_${p.to || 'end'}`;
}

const fmtShort = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
const fmtLong = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '-');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-');
const userName = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').name || ''; } catch (e) { return ''; }
};

function PeriodControls({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 3 + i);
  const update = (patch) => onChange({ ...value, ...patch });
  return (
    <div>
      <label className="text-xs font-bold text-gray-700 mb-1.5 block">Period</label>
      <div className="flex gap-1.5">
        <select value={value.mode} onChange={(e) => update({ mode: e.target.value })} className="border rounded-lg px-2 py-2 text-sm">
          <option value="month">Month</option>
          <option value="year">Year</option>
          <option value="day">Day</option>
          <option value="range">Custom</option>
        </select>
        {value.mode === 'month' && (
          <>
            <select value={value.month} onChange={(e) => update({ month: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0">
              {monthNames.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
            </select>
            <select value={value.year} onChange={(e) => update({ year: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm">
              {years.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </>
        )}
        {value.mode === 'year' && (
          <select value={value.year} onChange={(e) => update({ year: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm flex-1">
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        )}
        {value.mode === 'day' && (
          <input type="date" value={value.day} onChange={(e) => update({ day: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
        )}
        {value.mode === 'range' && (
          <>
            <input type="date" value={value.from} onChange={(e) => update({ from: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
            <input type="date" value={value.to} onChange={(e) => update({ to: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
          </>
        )}
      </div>
    </div>
  );
}

function AccountsBanner({ accounts }) {
  const down = (accounts || []).filter((a) => !a.ok);
  if (down.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
      {down.map((a) => a.system).join(', ')} could not be reached, so {down.length > 1 ? 'their' : 'its'} numbers are not included right now. Press Refresh to try again.
    </div>
  );
}

const badgeClass = (status) => {
  const v = String(status || '').toUpperCase();
  if (v === 'DESIGNED' || v === 'READY') return 'bg-green-100 text-green-700';
  if (v === 'PENDING' || v === 'EMPTY' || v === 'ENQUIRE') return 'bg-amber-100 text-amber-700';
  if (v === 'CANCEL' || v === 'NOT IN SYSTEM') return 'bg-red-100 text-red-600';
  return 'bg-gray-100 text-gray-600';
};

function SummaryCard({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <p className="text-xs text-gray-500">{label}</p>
      {value == null ? <p className="text-sm text-gray-400 mt-2">Not recorded</p> : <p className="text-2xl font-semibold mt-1">{value}</p>}
    </div>
  );
}

function FilesView() {
  const [period, setPeriod] = useState(defaultPeriod());
  const [account, setAccount] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [doc, setDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [copyMsg, setCopyMsg] = useState('');
  const detailRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/overview`, { cache: 'no-store' });
      const data = await res.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
    } catch (e) {
      setRows([]);
      setAccounts([]);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const { from, to } = periodBounds(period);
  const q = search.trim().toLowerCase();
  const lines = [];
  rows.forEach((r) => {
    if (r.date < from || r.date > to) return;
    systems.forEach((s) => {
      if (account && s !== account) return;
      let fl = r.files.filter((f) => f.system === s);
      if (q) fl = fl.filter((f) => String(f.name).toLowerCase().includes(q));
      if (fl.length === 0) return;
      lines.push({ key: `${r.date}-${r.batchNumber}-${s}`, date: r.date, batchNumber: r.batchNumber, system: s, files: fl, sheets: fl.reduce((sum, f) => sum + f.sheets, 0) });
    });
  });

  const openFile = async (line, f) => {
    setSelected({ key: line.key, fileUid: f.fileUid, system: f.system, name: f.name, sheets: f.sheets, sourceDeleted: f.sourceDeleted, date: line.date, batchNumber: line.batchNumber });
    setDoc(null);
    setDocError('');
    setDownloadError('');
    setDocLoading(true);
    setTimeout(() => { if (detailRef.current) detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 150);
    try {
      const res = await fetch(`${BASE}/file-detail?system=${f.system}&file_uid=${encodeURIComponent(f.fileUid)}`, { cache: 'no-store' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) setDocError(data.error || data.detail || 'Could not load this file.');
      else setDoc(data);
    } catch (e) {
      setDocError('Could not load this file.');
    }
    setDocLoading(false);
  };

  const detail = doc && doc.detail ? doc.detail : null;
  const orders = detail && Array.isArray(detail.orders) ? detail.orders : [];
  const ns = (detail && detail.not_selected) || { recorded: false, count: null, orders: [] };
  const notSelOrders = Array.isArray(ns.orders) ? ns.orders : [];
  const selectedCount = detail ? detail.selected : 0;
  const totalLabels = detail ? detail.total_labels : null;
  const notSelCount = ns.count != null ? ns.count : null;
  const fileSheets = doc && doc.sheet_count != null ? doc.sheet_count : selected ? selected.sheets : null;
  const sheetsKnown = !!(detail && detail.sheets_known);
  const sheetsSum = orders.reduce((sum, o) => sum + (o.sheets || 0), 0);
  const imagesSum = orders.reduce((sum, o) => sum + (o.image_count || 0), 0);
  const evenSplit = fileSheets != null && orders.length > 0 && fileSheets % orders.length === 0 ? fileSheets / orders.length : null;
  const estimated = orders.length > 1 && !sheetsKnown && evenSplit != null;
  const sheetOf = (o) => (o.sheets != null ? o.sheets : evenSplit);

  let checkText = '';
  let checkOk = null;
  if (orders.length > 0) {
    if (sheetsKnown && fileSheets != null) {
      checkOk = sheetsSum === fileSheets;
      checkText = checkOk ? `${sheetsSum} sheets in the table = ${fileSheets} sheets in the file` : `The table adds up to ${sheetsSum} sheets but the file has ${fileSheets}`;
    } else if (orders.length === 1 && fileSheets != null) {
      checkText = `This file has one order, so it has all ${fileSheets} sheets.`;
    } else if (evenSplit != null) {
      checkText = `Estimated: ${fileSheets} sheets divided by ${orders.length} orders = ${evenSplit} each. This file's record does not store sheets per order, so it cannot be checked.`;
    } else {
      checkText = `Sheets per order are not recorded for this file, and its ${fileSheets == null ? 'unknown' : fileSheets} sheets do not divide evenly across ${orders.length} orders.`;
    }
  }

  const copyNotSelected = () => {
    navigator.clipboard.writeText(notSelOrders.map((o) => o.order_id).join('\n'));
    setCopyMsg(`Copied ${notSelOrders.length} order ID(s)`);
    setTimeout(() => setCopyMsg(''), 2500);
  };

  const downloadPdf = async () => {
    if (!selected || !detail) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const na = (v) => (v == null ? 'N/A' : v);
      const made = orders.map((o) => [o.order_id, sheetOf(o) == null ? '' : sheetOf(o), o.image_count == null ? '' : o.image_count, o.design_status]);
      if (orders.length > 0) made.push(['TOTAL', sheetsKnown ? sheetsSum : fileSheets == null ? '' : fileSheets, imagesSum, '']);
      const pdfCheck = checkOk === true ? `OK: ${checkText}` : checkOk === false ? `WARNING: ${checkText}` : checkText;
      const sections = [
        {
          heading: 'MADE - in the file, sent to vendor',
          headers: ['Order ID', estimated ? 'Sheets (est.)' : 'Sheets', 'Images', 'Design Status'],
          rows: made,
          boldLast: orders.length > 0,
          note: pdfCheck,
          emptyText: 'No orders recorded for this file',
        },
        {
          heading: 'NOT SELECTED - not made, not sent',
          headers: ['Order ID', 'Design Status', 'Images', 'Reason'],
          rows: notSelOrders.map((o) => [o.order_id, o.design_status, o.image_count == null ? '' : o.image_count, o.reason || '']),
          emptyText: ns.recorded ? 'None' : 'Not recorded for this file',
        },
      ];
      const res = await fetch(`${BASE}/report-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: 'pdf',
          title: `${selected.name} - Orders`,
          subtitle: `${selected.system} | ${fmtLong(selected.date)} | B${selected.batchNumber}`,
          summary: `TOTAL LABELS IN PDF: ${na(totalLabels)} | SELECTED: ${selectedCount} | NOT SELECTED: ${na(notSelCount)}`,
          sections,
        }),
      });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${String(selected.name).replace(/[^\w.-]+/g, '_')}-orders.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError('Download failed — please try again.');
    }
    setDownloading(false);
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-xl font-semibold mb-1">Outsourced Work → Files</h1>
          <p className="text-gray-500 text-sm">Every synced file, kept even after the PDF is deleted. Click a file name to see its orders.</p>
        </div>
        <button onClick={load} disabled={loading} className={`${PRIMARY} text-white rounded-lg px-6 py-2.5 text-sm font-medium disabled:opacity-50`}>
          {loading ? 'Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
      <div className="mb-4"></div>

      <AccountsBanner accounts={accounts} />

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[320px]">
            <PeriodControls value={period} onChange={setPeriod} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Account</label>
            <select value={account} onChange={(e) => setAccount(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All Accounts</option>
              {systems.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="File name..." className="border rounded-lg px-3 py-2 text-sm w-full" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
              <th className="text-left px-3 py-2.5 font-normal">Date</th>
              <th className="text-left px-3 py-2.5 font-normal">Batch</th>
              <th className="text-left px-3 py-2.5 font-normal">Account</th>
              <th className="text-left px-3 py-2.5 font-normal">Files</th>
              <th className="text-right px-3 py-2.5 font-normal">Total PDFs</th>
              <th className="text-right px-3 py-2.5 font-normal">Sheets</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-gray-400 py-8 text-sm">Loading...</td></tr>
            ) : lines.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-gray-400 py-8 text-sm">No synced files for this period.</td></tr>
            ) : (
              lines.map((l) => (
                <tr key={l.key} className="border-b border-gray-50">
                  <td className="px-3 py-2.5">{fmtLong(l.date)}</td>
                  <td className="px-3 py-2.5">B{l.batchNumber}</td>
                  <td className="px-3 py-2.5 font-medium">{l.system}</td>
                  <td className="px-3 py-2.5 text-gray-600">
                    {l.files.map((f, i) => (
                      <span key={f.fileUid}>
                        {i > 0 && ', '}
                        <button onClick={() => openFile(l, f)} className={`text-blue-600 underline ${selected && selected.fileUid === f.fileUid ? 'bg-blue-100 rounded px-1' : ''}`}>{f.name}</button>
                        {` : ${f.sheets} SHEETS`}
                      </span>
                    ))}
                  </td>
                  <td className="px-3 py-2.5 text-right">{l.files.length}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{l.sheets}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div ref={detailRef} className="bg-white border border-gray-200 rounded-xl overflow-hidden mt-6">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">{selected.name} — {selected.system}</h2>
              <p className="text-xs text-gray-500">
                {fmtLong(selected.date)} · B{selected.batchNumber}
                {selected.sourceDeleted && <span className="ml-2 text-amber-700">Source file deleted from Production</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={downloadPdf} disabled={downloading || !detail} className="border border-gray-300 bg-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                {downloading ? 'Preparing...' : '⬇ Download PDF'}
              </button>
              <button onClick={() => setSelected(null)} className="border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">Close</button>
            </div>
          </div>
          {downloadError && <p className="text-xs text-red-600 px-4 pt-3">{downloadError}</p>}

          {docLoading ? (
            <p className="text-center text-gray-400 py-10 text-sm">Loading...</p>
          ) : docError ? (
            <p className="text-center text-red-600 py-10 text-sm">{docError}</p>
          ) : doc && !detail ? (
            <p className="text-center text-gray-500 py-10 text-sm">No order details were saved for this file when it was synced.</p>
          ) : detail ? (
            <div className="p-4">
              <div className="grid grid-cols-3 gap-3 mb-2">
                <SummaryCard label="Total labels in PDF" value={totalLabels} />
                <SummaryCard label="Selected" value={selectedCount} />
                <SummaryCard label="Not selected" value={notSelCount} />
              </div>
              <p className="text-xs text-gray-400 mb-4">Details are as they were when this file was synced. "Not recorded" means that number was not saved, so nothing is guessed.</p>

              <h3 className="text-sm font-semibold mt-2 mb-2">Section 1 — Made (in the file, sent to vendor)</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2.5 font-normal">Order ID</th>
                      <th className="text-right px-4 py-2.5 font-normal">Sheets{estimated ? ' (est.)' : ''}</th>
                      <th className="text-right px-4 py-2.5 font-normal">Images</th>
                      <th className="text-left px-4 py-2.5 font-normal">Design Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-gray-400 py-6 text-sm">{detail.note || 'No orders recorded for this file.'}</td></tr>
                    ) : (
                      <>
                        {orders.map((o, i) => (
                          <tr key={`${o.order_id}-${i}`} className="border-b border-gray-50">
                            <td className="px-4 py-2.5">{o.order_id}</td>
                            <td className="px-4 py-2.5 text-right">{sheetOf(o) == null ? '-' : sheetOf(o)}</td>
                            <td className="px-4 py-2.5 text-right">{o.image_count == null ? '-' : o.image_count}</td>
                            <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-3 py-1 ${badgeClass(o.design_status)}`}>{o.design_status}</span></td>
                          </tr>
                        ))}
                        <tr className="bg-gray-100 font-semibold">
                          <td className="px-4 py-2.5">TOTAL</td>
                          <td className="px-4 py-2.5 text-right">{sheetsKnown ? sheetsSum : fileSheets == null ? '-' : fileSheets}</td>
                          <td className="px-4 py-2.5 text-right">{imagesSum}</td>
                          <td className="px-4 py-2.5"></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {checkText && (
                <p className={`text-xs mt-2 ${checkOk === true ? 'text-green-700' : checkOk === false ? 'text-red-600' : 'text-gray-500'}`}>
                  {checkOk === true ? '✓ ' : checkOk === false ? '⚠ ' : ''}{checkText}
                </p>
              )}

              <div className="flex items-center justify-between mt-6 mb-2">
                <h3 className="text-sm font-semibold">Section 2 — Not selected (not made, not sent)</h3>
                <div className="flex items-center gap-2">
                  {copyMsg && <span className="text-xs text-green-700">{copyMsg}</span>}
                  <button onClick={copyNotSelected} disabled={notSelOrders.length === 0} className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50">Copy Not selected IDs</button>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2.5 font-normal">Order ID</th>
                      <th className="text-left px-4 py-2.5 font-normal">Design Status</th>
                      <th className="text-right px-4 py-2.5 font-normal">Images</th>
                      <th className="text-left px-4 py-2.5 font-normal">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notSelOrders.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-gray-400 py-6 text-sm">{ns.recorded ? 'None — every order was selected.' : 'Not recorded for this file.'}</td></tr>
                    ) : (
                      notSelOrders.map((o, i) => (
                        <tr key={`${o.order_id}-${i}`} className="border-b border-gray-50">
                          <td className="px-4 py-2.5">{o.order_id}</td>
                          <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-3 py-1 ${badgeClass(o.design_status)}`}>{o.design_status}</span></td>
                          <td className="px-4 py-2.5 text-right">{o.image_count == null ? '-' : o.image_count}</td>
                          <td className="px-4 py-2.5 text-gray-600">{o.reason}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default FilesView;
